import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  CalendarDays, CalendarPlus, Video, CheckCircle2, Clock, Loader2,
  DoorOpen, ArrowUpDown, PartyPopper, MessageCircle, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

const MIN = 60000;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

const calFmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

const googleCalUrl = (ev) => {
  const start = new Date(ev.event_date);
  const end = new Date(start.getTime() + (ev.duration_minutes || 60) * MIN);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&dates=${calFmt(start)}/${calFmt(end)}&details=${encodeURIComponent(ev.description || 'Evento di formazione Holiday World')}&location=${encodeURIComponent('Online - HW Smart')}`;
};

const outlookCalUrl = (ev) => {
  const start = new Date(ev.event_date);
  const end = new Date(start.getTime() + (ev.duration_minutes || 60) * MIN);
  return `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(ev.title)}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&body=${encodeURIComponent(ev.description || 'Evento di formazione Holiday World')}&location=${encodeURIComponent('Online - HW Smart')}`;
};

export default function EventiFormazione() {
  const token = localStorage.getItem('hw_token');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [registering, setRegistering] = useState(null);
  const [joining, setJoining] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [calendarPopup, setCalendarPopup] = useState(null);
  const [entryPopup, setEntryPopup] = useState(null);
  const [sortFut, setSortFut] = useState({ key: 'event_date', dir: 'asc' });
  const [sortPast, setSortPast] = useState({ key: 'event_date', dir: 'desc' });

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/training-events/mine`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setEvents(await res.json());
    } catch (e) { /* silenzioso */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchEvents();
    const t = setInterval(() => setNow(Date.now()), 30000);
    const r = setInterval(fetchEvents, 120000);
    return () => { clearInterval(t); clearInterval(r); };
  }, [fetchEvents]);

  const endOf = (ev) => new Date(ev.event_date).getTime() + (ev.duration_minutes || 60) * MIN;
  const future = events.filter((ev) => endOf(ev) >= now);
  const past = events.filter((ev) => endOf(ev) < now);

  const sortRows = (rows, sort) => {
    const val = (ev) => {
      if (sort.key === 'event_date') return new Date(ev.event_date).getTime();
      if (sort.key === 'duration_minutes') return ev.duration_minutes || 60;
      if (sort.key === 'reg') return ev.my_registration ? 1 : 0;
      return (ev.title || '').toLowerCase();
    };
    return [...rows].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * (sort.dir === 'asc' ? 1 : -1));
  };

  const handleRegister = async (ev) => {
    setRegistering(ev.id);
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}/register`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchEvents();
        setCalendarPopup(ev);
      } else toast.error(data.detail || 'Errore nella registrazione');
    } catch (e) { toast.error('Errore di connessione'); }
    setRegistering(null);
  };

  const handleJoin = async (ev) => {
    setJoining(ev.id);
    const w = window.open('', '_blank');
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}/join`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (w) w.location = data.meeting_url; else window.open(data.meeting_url, '_blank');
        toast.success('Buona riunione!');
        fetchEvents();
      } else {
        if (w) w.close();
        toast.error(data.detail || 'Impossibile entrare');
        fetchEvents();
      }
    } catch (e) { if (w) w.close(); toast.error('Errore di connessione'); }
    setJoining(null);
  };

  const handleRequestEntry = async (ev) => {
    setRequesting(ev.id);
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}/request-entry`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) { setEntryPopup(data.message); fetchEvents(); }
      else toast.error(data.detail || 'Errore nella richiesta');
    } catch (e) { toast.error('Errore di connessione'); }
    setRequesting(null);
  };

  const downloadIcs = async (ev) => {
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}/ics`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `evento_${ev.id.slice(0, 8)}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore nel download'); }
  };

  const SortHeader = ({ label, k, sort, setSort }) => (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-[#1E3A8A] uppercase cursor-pointer select-none hover:bg-blue-50"
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'asc' ? 'desc' : 'asc' }))}
      data-testid={`sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="h-3 w-3" /></span>
    </th>
  );

  const FutureRow = ({ ev }) => {
    const start = new Date(ev.event_date).getTime();
    const reg = ev.my_registration;
    const opensAt = ev.join_opens_at ? new Date(ev.join_opens_at).getTime() : start - 15 * MIN;
    const closesAt = ev.join_closes_at ? new Date(ev.join_closes_at).getTime() : start + 15 * MIN;
    const inWindow = now >= opensAt && now <= closesAt;
    const canJoin = reg && reg.token_status === 'active' && inWindow;
    const canRequest = reg && now >= start - 15 * MIN && now <= endOf(ev) + 15 * MIN &&
      (reg.token_status === 'used' || now > start + 15 * MIN);
    return (
      <tr className="border-b border-slate-100 hover:bg-blue-50/40" data-testid={`event-row-${ev.id}`}>
        <td className="px-3 py-3 text-sm text-[#1E3A8A] font-medium whitespace-nowrap">{fmtDate(ev.event_date)}</td>
        <td className="px-3 py-3">
          <p className="text-sm font-semibold text-[#1E3A8A]">{ev.title}</p>
          {ev.description && <p className="text-xs text-slate-500 line-clamp-2 max-w-md">{ev.description}</p>}
        </td>
        <td className="px-3 py-3 text-sm text-[#1E3A8A] whitespace-nowrap">{ev.duration_minutes || 60} min</td>
        <td className="px-3 py-3">
          {reg ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full">
              <CheckCircle2 className="h-3.5 w-3.5" /> Registrato
            </span>
          ) : (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">Non registrato</span>
          )}
        </td>
        <td className="px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {!reg && ev.registration_open !== false && now < start && (
              <Button size="sm" onClick={() => handleRegister(ev)} disabled={registering === ev.id}
                className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white" data-testid={`register-btn-${ev.id}`}>
                {registering === ev.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrati'}
              </Button>
            )}
            {reg && (
              <Button size="sm" variant="outline" onClick={() => setCalendarPopup(ev)}
                className="border-blue-300 text-[#1E3A8A]" data-testid={`calendar-btn-${ev.id}`}>
                <CalendarPlus className="h-4 w-4 mr-1" /> Calendario
              </Button>
            )}
            {reg && reg.token_status === 'active' && !canRequest && (
              <Button size="sm" onClick={() => handleJoin(ev)} disabled={!canJoin || joining === ev.id}
                title={canJoin ? 'Entra nella riunione (monouso)' : 'Attivo da 15 minuti prima a 15 minuti dopo l\'inizio'}
                className="bg-green-600 hover:bg-green-700 text-white disabled:bg-slate-300"
                data-testid={`join-btn-${ev.id}`}>
                {joining === ev.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Video className="h-4 w-4 mr-1" /> Entra nella riunione</>}
              </Button>
            )}
            {canRequest && (
              <Button size="sm" onClick={() => handleRequestEntry(ev)} disabled={requesting === ev.id}
                className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`request-entry-btn-${ev.id}`}>
                {requesting === ev.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><DoorOpen className="h-4 w-4 mr-1" /> Richiedi ingresso</>}
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1E3A8A]" /></div>;
  }

  return (
    <div className="space-y-6 p-1" data-testid="eventi-formazione-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1E3A8A] flex items-center gap-2">
          <CalendarDays className="h-6 w-6" /> Eventi e Riunioni
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Formazione e riunioni organizzate dall'ufficio. Registrati per ricevere promemoria e il link di ingresso personale (monouso).
        </p>
      </div>

      <Card className="overflow-hidden">
        <div className="bg-[#1E3A8A] px-4 py-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-white" />
          <h2 className="text-white font-semibold text-sm">Eventi futuri ({future.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="future-events-table">
            <thead className="bg-blue-50/60">
              <tr>
                <SortHeader label="Data" k="event_date" sort={sortFut} setSort={setSortFut} />
                <SortHeader label="Evento" k="title" sort={sortFut} setSort={setSortFut} />
                <SortHeader label="Durata" k="duration_minutes" sort={sortFut} setSort={setSortFut} />
                <SortHeader label="Registrazione" k="reg" sort={sortFut} setSort={setSortFut} />
                <th className="px-3 py-2 text-left text-xs font-semibold text-[#1E3A8A] uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(future, sortFut).map((ev) => <FutureRow key={ev.id} ev={ev} />)}
              {future.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">Nessun evento in programma</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="bg-slate-600 px-4 py-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-white" />
          <h2 className="text-white font-semibold text-sm">Eventi passati ({past.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="past-events-table">
            <thead className="bg-slate-50">
              <tr>
                <SortHeader label="Data" k="event_date" sort={sortPast} setSort={setSortPast} />
                <SortHeader label="Evento" k="title" sort={sortPast} setSort={setSortPast} />
                <SortHeader label="Durata" k="duration_minutes" sort={sortPast} setSort={setSortPast} />
                <th className="px-3 py-2 text-left text-xs font-semibold text-[#1E3A8A] uppercase">Partecipazione</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(past, sortPast).map((ev) => (
                <tr key={ev.id} className="border-b border-slate-100" data-testid={`past-event-row-${ev.id}`}>
                  <td className="px-3 py-3 text-sm text-[#1E3A8A] whitespace-nowrap">{fmtDate(ev.event_date)}</td>
                  <td className="px-3 py-3 text-sm font-semibold text-[#1E3A8A]">{ev.title}</td>
                  <td className="px-3 py-3 text-sm text-[#1E3A8A]">{ev.duration_minutes || 60} min</td>
                  <td className="px-3 py-3">
                    {ev.my_registration?.attended ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" /> Presente</span>
                    ) : ev.my_registration ? (
                      <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full">Registrato, non entrato</span>
                    ) : (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">Non registrato</span>
                    )}
                  </td>
                </tr>
              ))}
              {past.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-400">Nessun evento passato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Popup registrazione + calendario */}
      <Dialog open={!!calendarPopup} onOpenChange={(o) => !o && setCalendarPopup(null)}>
        <DialogContent className="max-w-md" data-testid="calendar-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3A8A]">
              <PartyPopper className="h-5 w-5 text-green-600" /> Grazie per la registrazione!
            </DialogTitle>
            <DialogDescription>
              Sei registrato a <strong>{calendarPopup?.title}</strong> — {calendarPopup && fmtDate(calendarPopup.event_date)}.
              Aggiungilo al tuo calendario:
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button asChild className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white justify-start" data-testid="gcal-btn">
              <a href={calendarPopup ? googleCalUrl(calendarPopup) : '#'} target="_blank" rel="noreferrer">
                <CalendarPlus className="h-4 w-4 mr-2" /> Google Calendar
              </a>
            </Button>
            <Button asChild className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white justify-start" data-testid="outlook-btn">
              <a href={calendarPopup ? outlookCalUrl(calendarPopup) : '#'} target="_blank" rel="noreferrer">
                <CalendarPlus className="h-4 w-4 mr-2" /> Outlook
              </a>
            </Button>
            <Button onClick={() => calendarPopup && downloadIcs(calendarPopup)} variant="outline"
              className="border-blue-300 text-[#1E3A8A] justify-start" data-testid="ics-btn">
              <Download className="h-4 w-4 mr-2" /> Apple / altri calendari (.ics)
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Riceverai un promemoria il giorno prima e, nell'ora precedente, l'email col pulsante personale, utilizzabile da 15 minuti prima (orario italiano).
          </p>
        </DialogContent>
      </Dialog>

      {/* Popup richiesta ingresso */}
      <Dialog open={!!entryPopup} onOpenChange={(o) => !o && setEntryPopup(null)}>
        <DialogContent className="max-w-md" data-testid="request-entry-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3A8A]">
              <DoorOpen className="h-5 w-5 text-amber-600" /> Richiesta inviata
            </DialogTitle>
          </DialogHeader>
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 text-sm text-[#1E3A8A] flex gap-2">
            <MessageCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p>{entryPopup}</p>
          </div>
          <Button onClick={() => setEntryPopup(null)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white" data-testid="close-entry-popup-btn">
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
